import {useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Boxes, ChevronLeft, ChevronRight, Download, Filter, PackagePlus, Search, X} from 'lucide-react'
import * as XLSX from 'xlsx'
import {ProductThumbnail} from '../shared/components/ProductThumbnail'
import {CardHelp} from '../shared/components/CardHelp'
import {ProductWithCalculations, StatusType} from '../shared/types/inventory'

export interface InventoryAnalysisItem extends ProductWithCalculations {
  cnpj: string
  razaoSocial: string
}

interface InventoryAnalysisModalProps {
  mode: 'coverage' | 'orders'
  products: InventoryAnalysisItem[]
  targetCoverage: number
  onClose: () => void
}

const PAGE_SIZE = 100
type AnalysisColumn = 'product' | 'cnpj' | 'supplier' | 'stock' | 'cmd' | 'coverage' | 'leadTime' | 'target' | 'order' | 'status'
type SortDirection = 'asc' | 'desc'

const DEFAULT_COLUMN_WIDTHS: Record<AnalysisColumn, number> = {
  product: 180, cnpj: 165, supplier: 185, stock: 100, cmd: 90,
  coverage: 115, leadTime: 105, target: 115, order: 135, status: 155,
}
const MIN_COLUMN_WIDTHS: Record<AnalysisColumn, number> = {
  product: 140, cnpj: 120, supplier: 130, stock: 80, cmd: 70,
  coverage: 90, leadTime: 90, target: 95, order: 110, status: 120,
}

const statusLabels: Record<StatusType, string> = {
  ruptura: 'Ruptura instalada',
  iminente: 'Ruptura iminente',
  minimo: 'Perto do mínimo',
  critica: 'Cobertura crítica',
  excesso: 'Excesso',
  saudavel: 'Saudável',
}
const statusRowClasses: Record<StatusType, string> = {
  ruptura: 'bg-red-50/80 hover:bg-red-100/80',
  iminente: 'bg-orange-50/80 hover:bg-orange-100/80',
  minimo: 'bg-amber-50/80 hover:bg-amber-100/80',
  critica: 'bg-cyan-50/80 hover:bg-cyan-100/80',
  excesso: 'bg-blue-50/70 hover:bg-blue-100/70',
  saudavel: 'bg-emerald-50/60 hover:bg-emerald-100/70',
}


function formatCnpj(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 14) return value
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

function formatNumber(value: number, digits = 0) {
  return new Intl.NumberFormat('pt-BR', {maximumFractionDigits: digits, minimumFractionDigits: digits}).format(value || 0)
}

export function InventoryAnalysisModal({mode, products, targetCoverage, onClose}: InventoryAnalysisModalProps) {
  const [search, setSearch] = useState('')
  const [cnpj, setCnpj] = useState('all')
  const [supplier, setSupplier] = useState('all')
  const [status, setStatus] = useState<'all' | StatusType>('all')
  const [analysisFilter, setAnalysisFilter] = useState('all')
  const [colorRows, setColorRows] = useState(true)
  const [sortKey, setSortKey] = useState<AnalysisColumn>(mode === 'coverage' ? 'coverage' : 'order')
  const [sortDirection, setSortDirection] = useState<SortDirection>(mode === 'coverage' ? 'asc' : 'desc')
  const [columnWidths, setColumnWidths] = useState<Record<AnalysisColumn, number>>(DEFAULT_COLUMN_WIDTHS)
  const [page, setPage] = useState(1)

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [onClose])

  const cnpjs = useMemo(() => Array.from(new Set(products.map((product) => product.cnpj))).sort(), [products])
  const suppliers = useMemo(() => Array.from(new Set(products.map((product) => product.fornecedor).filter(Boolean) as string[])).sort(), [products])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    return products
      .filter((product) => mode === 'coverage' ? product.cmd > 0 : product.pedidoSugerido > 0)
      .filter((product) => !term || `${product.nome} ${product.ean} ${product.fornecedor || ''}`.toLocaleLowerCase('pt-BR').includes(term))
      .filter((product) => cnpj === 'all' || product.cnpj === cnpj)
      .filter((product) => supplier === 'all' || (supplier === 'registered' ? Boolean(product.fornecedor?.trim()) : product.fornecedor === supplier))
      .filter((product) => status === 'all' || product.status === status)
      .filter((product) => {
        if (analysisFilter === 'all') return true
        if (mode === 'coverage') {
          if (analysisFilter === 'critical') return product.cobertura < (product.leadTime || 0)
          if (analysisFilter === 'below_target') return product.cobertura < targetCoverage
          if (analysisFilter === 'zero_stock') return product.estoqueAtual <= 0
          return true
        }
        if (analysisFilter === 'urgent') return product.status === 'ruptura' || product.status === 'iminente'
        return product.pedidoSugerido >= Number(analysisFilter)
      })
      .sort((a, b) => {
        const value = (product: InventoryAnalysisItem): string | number => {
          if (sortKey === 'product') return product.nome.toLocaleLowerCase('pt-BR')
          if (sortKey === 'cnpj') return product.cnpj
          if (sortKey === 'supplier') return (product.fornecedor || '').toLocaleLowerCase('pt-BR')
          if (sortKey === 'stock') return product.estoqueAtual
          if (sortKey === 'cmd') return product.cmd
          if (sortKey === 'coverage') return product.cobertura
          if (sortKey === 'leadTime') return product.leadTime || 0
          if (sortKey === 'target') return product.estoqueAlvo
          if (sortKey === 'order') return product.pedidoSugerido
          return statusLabels[product.status]
        }
        const valueA = value(a)
        const valueB = value(b)
        const comparison = typeof valueA === 'string'
          ? valueA.localeCompare(String(valueB), 'pt-BR')
          : valueA - Number(valueB)
        return sortDirection === 'asc' ? comparison : -comparison
      })
  }, [analysisFilter, cnpj, mode, products, search, sortDirection, sortKey, status, supplier, targetCoverage])

  useEffect(() => setPage(1), [analysisFilter, cnpj, search, sortDirection, sortKey, status, supplier])

  const summary = useMemo(() => {
    const totalStock = filtered.reduce((sum, product) => sum + product.estoqueAtual, 0)
    const urgent = filtered.filter((product) => product.status === 'ruptura' || product.status === 'iminente').length
    if (mode === 'coverage') {
      const average = filtered.length ? filtered.reduce((sum, product) => sum + product.cobertura, 0) / filtered.length : 0
      return {primary: average, secondary: urgent, tertiary: totalStock}
    }
    const units = filtered.reduce((sum, product) => sum + product.pedidoSugerido, 0)
    const target = filtered.reduce((sum, product) => sum + product.estoqueAlvo, 0)
    return {primary: units, secondary: urgent, tertiary: target}
  }, [filtered, mode])

  const handleSort = (column: AnalysisColumn) => {
    if (sortKey === column) setSortDirection((current) => current === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(column)
      setSortDirection(['product', 'cnpj', 'supplier', 'status'].includes(column) ? 'asc' : 'desc')
    }
  }

  const startResize = (column: AnalysisColumn, event: ReactMouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const startX = event.clientX
    const startWidth = columnWidths[column]
    const onMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(MIN_COLUMN_WIDTHS[column], startWidth + moveEvent.clientX - startX)
      setColumnWidths((current) => ({...current, [column]: nextWidth}))
    }
    const onEnd = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onEnd)
  }

  const tableWidth = Object.values(columnWidths).reduce((sum, width) => sum + width, 0)
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const exportXlsx = () => {
    const rows = filtered.map((product) => ({
      CNPJ: product.cnpj,
      Produto: product.nome,
      EAN: product.ean,
      Fornecedor: product.fornecedor || '',
      Status: statusLabels[product.status],
      'Estoque atual': product.estoqueAtual,
      'Consumo médio diário': product.cmd,
      'Cobertura (dias)': product.cobertura,
      'Lead time (dias)': product.leadTime || 0,
      'Estoque de seguranca': product.ss,
      'Ponto de pedido': product.rop,
      'Estoque alvo': product.estoqueAlvo,
      'Pedido sugerido': product.pedidoSugerido,
    }))
    const worksheet = XLSX.utils.json_to_sheet(rows)
    worksheet['!cols'] = [
      {wch: 20}, {wch: 48}, {wch: 18}, {wch: 28}, {wch: 22},
      {wch: 15}, {wch: 20}, {wch: 18}, {wch: 18}, {wch: 22},
      {wch: 18}, {wch: 16}, {wch: 18},
    ]
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, mode === 'coverage' ? 'Menor cobertura' : 'Pedidos sugeridos')
    XLSX.writeFile(workbook, `${mode === 'coverage' ? 'menor_cobertura' : 'maiores_pedidos'}_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  if (typeof document === 'undefined') return null

  const title = mode === 'coverage' ? 'Análise de menor cobertura' : 'Análise de maiores pedidos sugeridos'
  const description = mode === 'coverage'
    ? 'Produtos ordenados pelos menores dias de cobertura.'
    : 'Produtos ordenados pela maior necessidade de reposição.'

  return createPortal(
    <div className='fixed inset-0 z-[21000] flex h-[100dvh] items-center justify-center overflow-y-auto bg-slate-950/60 px-2 py-6 backdrop-blur-sm sm:px-4 sm:py-8' onMouseDown={onClose}>
      <div className='flex max-h-[calc(100dvh-3rem)] min-h-0 w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl sm:max-h-[calc(100dvh-4rem)]' onMouseDown={(event) => event.stopPropagation()}>
        <header className='flex flex-none items-start justify-between gap-5 border-b border-slate-200 bg-white px-5 py-4 sm:px-7'>
          <div className='flex min-w-0 items-start gap-4'>
            <span className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl ${mode === 'coverage' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
              {mode === 'coverage' ? <AlertTriangle className='h-5 w-5' /> : <PackagePlus className='h-5 w-5' />}
            </span>
            <div className='min-w-0'>
              <h2 className='text-xl font-bold text-slate-950'>{title}</h2>
              <p className='mt-1 text-sm text-slate-500'>{description} {filtered.length} produtos encontrados.</p>
            </div>
          </div>
          <div className='flex flex-none items-center gap-2'>
            <button type='button' onClick={exportXlsx} disabled={!filtered.length} className='inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-40'>
              <Download className='h-4 w-4' /> Exportar XLSX
            </button>
            <button type='button' onClick={onClose} className='rounded-xl p-2.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700' aria-label='Fechar'>
              <X className='h-5 w-5' />
            </button>
          </div>
        </header>

        <div className='min-h-0 flex-1 overflow-y-auto p-4 sm:p-6'>
          <div className='mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
            <MetricCard label='Produtos filtrados' value={formatNumber(filtered.length)} help='Quantidade de produtos que permanecem após a aplicação dos filtros do modal.' icon={<Boxes className='h-5 w-5' />} />
            <MetricCard label={mode === 'coverage' ? 'Cobertura média' : 'Unidades sugeridas'} value={mode === 'coverage' ? `${formatNumber(summary.primary, 1)} dias` : formatNumber(summary.primary)} help={mode === 'coverage' ? 'Média de estoque atual dividido pelo consumo médio diário dos produtos filtrados.' : 'Soma das quantidades sugeridas para reposição nos produtos filtrados.'} />
            <MetricCard label='Produtos urgentes' value={formatNumber(summary.secondary)} help='Produtos classificados em ruptura instalada ou ruptura iminente.' />
            <MetricCard label={mode === 'coverage' ? 'Estoque atual' : 'Estoque alvo'} value={`${formatNumber(summary.tertiary)} un`} help={mode === 'coverage' ? 'Soma do estoque atual dos produtos filtrados.' : 'Soma do estoque planejado dos produtos filtrados.'} />
          </div>

          <section className='mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'>
            <div className='mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700'><Filter className='h-4 w-4 text-teal-600' /> Filtros da análise</div>
            <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-5 2xl:grid-cols-7'>
              <label className='relative xl:col-span-2'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Produto, EAN ou fornecedor' className='w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100' />
              </label>
              <AnalysisSelect value={cnpj} onChange={setCnpj}><option value='all'>Todos os CNPJs</option>{cnpjs.map((item) => <option key={item} value={item}>{formatCnpj(item)}</option>)}</AnalysisSelect>
              <AnalysisSelect value={supplier} onChange={setSupplier}><option value='all'>Todos os fornecedores</option><option value='registered'>Somente fornecedores cadastrados</option>{suppliers.map((item) => <option key={item} value={item}>{item}</option>)}</AnalysisSelect>
              <AnalysisSelect value={status} onChange={(value) => setStatus(value as typeof status)}><option value='all'>Todos os status</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</AnalysisSelect>
              <AnalysisSelect value={analysisFilter} onChange={setAnalysisFilter}>
                <option value='all'>{mode === 'coverage' ? 'Toda cobertura' : 'Todo pedido sugerido'}</option>
                {mode === 'coverage' ? <>
                  <option value='critical'>Abaixo do lead time</option>
                  <option value='below_target'>Abaixo da cobertura alvo</option>
                  <option value='zero_stock'>Sem estoque</option>
                </> : <>
                  <option value='urgent'>Somente urgentes</option>
                  <option value='10'>Pedido de 10+ unidades</option>
                  <option value='50'>Pedido de 50+ unidades</option>
                  <option value='100'>Pedido de 100+ unidades</option>
                </>}
              </AnalysisSelect>
              <AnalysisSelect value={colorRows ? 'status' : 'none'} onChange={(value) => setColorRows(value === 'status')}>
                <option value='status'>Colorir linhas por status</option>
                <option value='none'>N&atilde;o colorir as linhas</option>
              </AnalysisSelect>
            </div>
          </section>

          <section className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'>
            <div className='overflow-x-auto'>
              <table className='w-full table-fixed' style={{minWidth: tableWidth}}>
                <colgroup>
                  {(Object.keys(columnWidths) as AnalysisColumn[]).map((column) => <col key={column} style={{width: columnWidths[column]}} />)}
                </colgroup>
                <thead className='bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500'>
                  <tr>
                    <AnalysisHeader column='product' label='Produto' width={columnWidths.product} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} />
                    <AnalysisHeader column='cnpj' label='CNPJ' width={columnWidths.cnpj} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} />
                    <AnalysisHeader column='supplier' label='Fornecedor' width={columnWidths.supplier} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} />
                    <AnalysisHeader column='stock' label='Estoque' width={columnWidths.stock} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} align='right' />
                    <AnalysisHeader column='cmd' label='CMD' width={columnWidths.cmd} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} align='right' />
                    <AnalysisHeader column='coverage' label='Cobertura' width={columnWidths.coverage} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} align='right' />
                    <AnalysisHeader column='leadTime' label='Lead time' width={columnWidths.leadTime} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} align='right' />
                    <AnalysisHeader column='target' label='Estoque alvo' width={columnWidths.target} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} align='right' />
                    <AnalysisHeader column='order' label='Pedido sugerido' width={columnWidths.order} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} align='right' />
                    <AnalysisHeader column='status' label='Status' width={columnWidths.status} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} onResize={startResize} />
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {visible.map((product) => <tr key={`${product.cnpj}-${product.ean}`} className={`transition-colors ${colorRows ? statusRowClasses[product.status] : 'bg-white hover:bg-slate-50'}`}>
                    <td className='overflow-hidden px-4 py-3'><div className='flex min-w-0 items-center gap-3'><ProductThumbnail src={product.foto} alt={product.nome} ean={product.ean} size='sm' details={{
                      status: product.status, statusLabel: statusLabels[product.status], estoqueAtual: product.estoqueAtual,
                      cmd: product.cmd, cobertura: product.cobertura, leadTime: product.leadTime,
                      estoqueAlvo: product.estoqueAlvo, pedidoSugerido: product.pedidoSugerido, fornecedor: product.fornecedor,
                    }} /><div className='min-w-0'><p className='truncate font-semibold text-slate-900'>{product.nome}</p><p className='mt-0.5 font-mono text-xs text-slate-500'>EAN {product.ean}</p></div></div></td>
                    <td className='px-4 py-3 font-mono text-xs text-slate-600'>{formatCnpj(product.cnpj)}</td>
                    <td className='overflow-hidden truncate px-4 py-3 text-sm text-slate-600'>{product.fornecedor || 'Sem fornecedor'}</td>
                    <td className='px-4 py-3 text-right font-semibold tabular-nums'>{formatNumber(product.estoqueAtual)}</td>
                    <td className='px-4 py-3 text-right tabular-nums'>{formatNumber(product.cmd, 2)}</td>
                    <td className={`px-4 py-3 text-right font-semibold tabular-nums ${product.cobertura < (product.leadTime || 0) ? 'text-red-600' : 'text-slate-800'}`}>{formatNumber(product.cobertura, 1)}d</td>
                    <td className='px-4 py-3 text-right tabular-nums'>{formatNumber(product.leadTime || 0, 1)}d</td>
                    <td className='px-4 py-3 text-right tabular-nums'>{formatNumber(product.estoqueAlvo)}</td>
                    <td className='px-4 py-3 text-right font-bold tabular-nums text-blue-600'>{formatNumber(product.pedidoSugerido)}</td>
                    <td className='px-4 py-3'><span className='whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700'>{statusLabels[product.status]}</span></td>
                  </tr>)}
                  {!visible.length && <tr><td colSpan={10} className='px-6 py-16 text-center text-sm text-slate-500'>Nenhum produto corresponde aos filtros selecionados.</td></tr>}
                </tbody>
              </table>
            </div>
            <footer className='flex flex-col gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
              <p className='text-sm text-slate-500'>Exibindo {visible.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0}-{Math.min(currentPage * PAGE_SIZE, filtered.length)} de {filtered.length}</p>
              <div className='flex items-center gap-2'>
                <button type='button' disabled={currentPage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))} className='rounded-lg border border-slate-300 p-2 disabled:opacity-40'><ChevronLeft className='h-4 w-4' /></button>
                <span className='min-w-24 text-center text-sm font-semibold'>{currentPage} de {pageCount}</span>
                <button type='button' disabled={currentPage === pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))} className='rounded-lg border border-slate-300 p-2 disabled:opacity-40'><ChevronRight className='h-4 w-4' /></button>
              </div>
            </footer>
          </section>
        </div>
      </div>
    </div>,
    document.body
  )
}

function AnalysisHeader({column, label, width, sortKey, sortDirection, onSort, onResize, align = 'left'}: {
  column: AnalysisColumn
  label: string
  width: number
  sortKey: AnalysisColumn
  sortDirection: SortDirection
  onSort: (column: AnalysisColumn) => void
  onResize: (column: AnalysisColumn, event: ReactMouseEvent<HTMLSpanElement>) => void
  align?: 'left' | 'right'
}) {
  const active = sortKey === column
  const Icon = !active ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown
  return (
    <th style={{width}} onClick={() => onSort(column)} className={`relative select-none px-4 py-3 transition-colors hover:bg-slate-100 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <span className={`flex cursor-pointer items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}<Icon className={`h-3.5 w-3.5 flex-none ${active ? 'text-teal-600' : 'text-slate-300'}`} />
      </span>
      <span role='separator' aria-label={`Redimensionar coluna ${label}`} title='Arraste para redimensionar' onMouseDown={(event) => onResize(column, event)} onClick={(event) => event.stopPropagation()} className='absolute right-0 top-0 h-full w-2 cursor-col-resize border-r border-transparent hover:border-teal-400' />
    </th>
  )
}
function AnalysisSelect({value, onChange, children}: {value: string; onChange: (value: string) => void; children: ReactNode}) {
  return <select value={value} onChange={(event) => onChange(event.target.value)} className='rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100'>{children}</select>
}

function MetricCard({label, value, help, icon}: {label: string; value: string; help: string; icon?: ReactNode}) {
  return <div className='relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'><CardHelp content={help} className='absolute right-2 top-2' /><div className='flex items-center justify-between gap-3 pr-7'><div><p className='text-xs font-semibold uppercase tracking-wide text-slate-400'>{label}</p><p className='mt-1 text-2xl font-bold text-slate-900'>{value}</p></div>{icon && <span className='rounded-xl bg-teal-50 p-2.5 text-teal-600'>{icon}</span>}</div></div>
}
